import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/**
 * La curatela della pagina stagione, lato frontend.
 *
 * Il backend da' idee coi pezzi tirati a sorte (di categorie miste) e 48 pezzi
 * in ordine di sku, dove le varianti di una serie stanno in fila. Qui: le idee
 * del Natale pescano dal pool Natale-Tavola (tirato a sorte a ogni caricamento
 * della pagina), e un solo registro per tutta la pagina fa si' che un pezzo o
 * una foto compaiano una volta sola.
 */

/** Che cosa e' il pezzo: il titolo fino alla prima virgola («Anello porta tovagliolo»). */
export const tipo = (p) => (p?.title || '').split(',')[0].trim().toLowerCase();

const immagine = (p) => p?.images?.[0] || null;

/**
 * Il registro della pagina: `prendi(p)` dice si' la prima volta che vede uno
 * sku e la sua immagine, no a ogni ripetizione (stesso sku o stessa foto).
 */
export const registro = () => {
  const visti = new Set();
  return (p) => {
    const chiavi = [p?.sku, immagine(p)].filter(Boolean);
    if (!p?.sku || chiavi.some((k) => visti.has(k))) return false;
    chiavi.forEach((k) => visti.add(k));
    return true;
  };
};

/** Un pezzo di Natale, famiglia Tavola: niente monouso, niente pezzi senza foto. */
export const tavolaNatale = (p) =>
  (p?.category || '').toLowerCase() === 'tavola e cucina' &&
  p.subcategory !== 'MONOUSO NATALIZIO' &&
  !/monouso/i.test(p.it?.title || p.title || '') &&
  !!immagine(p);

/** Mescola senza toccare l'originale (Fisher-Yates); `rng` torna un numero in [0, 1). */
const mescola = (lista, rng) => {
  const a = [...lista];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/** Stesso tipo di pezzo, o stessa collezione: due cosi' nella stessa idea si somigliano troppo. */
const simili = (a, b) => tipo(a) === tipo(b) || (!!a.collection && a.collection === b.collection);

/**
 * Dal pool, `idee` gruppi di `per` pezzi tutti diversi (sku e foto), a sorte.
 * In ogni gruppo si evitano due pezzi dello stesso tipo o della stessa
 * collezione, e in tutta la sezione due pezzi dello stesso tipo (le dodici
 * tazze Oroscocats non devono riempire la pagina); se il pool non basta per
 * evitarli si prende quel che c'e'. `rng` si inietta per i test.
 */
export const estraiIdee = (pool = [], rng = Math.random, idee = 3, per = 3) => {
  const prendi = registro();
  const resto = mescola(pool.filter((p) => prendi(p)), rng);
  const usati = new Set();
  return Array.from({ length: idee }, () => {
    const gruppo = [];
    const pesca = (ok) => {
      for (let i = 0; i < resto.length && gruppo.length < per; ) {
        if (ok(resto[i])) {
          usati.add(tipo(resto[i]));
          gruppo.push(...resto.splice(i, 1));
        } else i += 1;
      }
    };
    pesca((p) => !usati.has(tipo(p)) && !gruppo.some((g) => simili(g, p)));
    pesca((p) => !gruppo.some((g) => simili(g, p)));
    pesca(() => true);
    return gruppo;
  });
};

/**
 * Le tre idee coi loro pezzi: prima quelli estratti dal pool (`scelte[i]`),
 * poi, a riserva, quelli del backend che `ammesso` accetta (per Natale:
 * `tavolaNatale`). Al massimo `quanti` per idea, ognuno una volta sola nella pagina.
 */
export const curaIdee = (ideas = [], scelte = [], prendi = registro(), quanti = 3, ammesso = () => true) =>
  ideas.map((idea, i) => {
    const candidati = [...(scelte[i] || []), ...(idea.products || []).filter(ammesso)];
    const products = [];
    for (const p of candidati) {
      if (products.length === quanti) break;
      if (prendi(p)) products.push(p);
    }
    return { ...idea, products };
  });

/** Prima la tavola, poi i tessili, poi il resto; dentro ogni gruppo l'ordine del feed. */
const RANGO = { 'tavola e cucina': 0, tessile: 1 };
const rango = (p) => (/monouso/i.test(p.it?.title || p.title || '') ? 2 : RANGO[(p.category || '').toLowerCase()] ?? 2);

/** Mai due titoli uguali uno dopo l'altro: si tira avanti il primo diverso. */
export const alterna = (lista) => {
  const resto = [...lista];
  const out = [];
  while (resto.length) {
    const prima = out.length ? tipo(out[out.length - 1]) : null;
    const i = resto.findIndex((p) => tipo(p) !== prima);
    out.push(...resto.splice(i === -1 ? 0 : i, 1));
  }
  return out;
};

/**
 * I pezzi delle feste, senza quelli gia' sulla pagina. Prima la tavola, poi i
 * tessili, poi il resto; dentro ogni gruppo un giro per tipo (un anello, una
 * ciotola, un servizio, poi il secondo anello...), cosi' le prime file non
 * sono quattro portatovaglioli. Alla fine nessun tipo uguale in fila.
 */
export const curaPezzi = (products = [], prendi = registro()) => {
  const giri = new Map();
  const righe = products.filter((p) => prendi(p)).map((p, i) => {
    const k = `${rango(p)}|${tipo(p)}`;
    const giro = giri.get(k) || 0;
    giri.set(k, giro + 1);
    return { p, r: rango(p), giro, i };
  });
  righe.sort((a, b) => a.r - b.r || a.giro - b.giro || a.i - b.i);
  return alterna(righe.map((x) => x.p));
};

/** Le categorie dei pezzi davvero arrivati (almeno 2 pezzi), in minuscolo con la prima maiuscola. */
export const categorieDi = (products = []) => {
  const conta = new Map();
  products.forEach((p) => {
    const c = (p.category || '').trim().toLowerCase();
    if (c) conta.set(c, (conta.get(c) || 0) + 1);
  });
  return [...conta]
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([c, n]) => [c.charAt(0).toUpperCase() + c.slice(1), n]);
};

/**
 * Il pool Natale-Tavola: tutti i pezzi della famiglia Tavola e cucina di
 * Natale (una pagina da 96 li contiene), senza monouso e senza pezzi senza foto.
 * Se la chiamata fallisce `data` resta vuoto e le idee ripiegano sul backend.
 */
export const usePezziTavolaNatale = (enabled = true, lang = 'it') =>
  useQuery({
    queryKey: ['season-tavola-natale', lang],
    queryFn: async () => {
      const { data } = await api.get('/products', {
        params: { macro: 'Natale', category: 'TAVOLA E CUCINA', per_page: 96, lang },
      });
      return (data?.items || []).filter(tavolaNatale);
    },
    enabled,
    staleTime: 30 * 60_000,
  });
